// src/context/CurrencyContext.js - FIXED for PWA reliability
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../utils/constants';

const CurrencyContext = createContext();

const CURRENCY_TIMEOUT = 8000; // 8 seconds TOTAL timeout (not per retry)
const MAX_RETRIES = 1; // Reduced to 1 retry
const RETRY_DELAY = 1000; // 1 second between retries

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState('NGN');
  const [loading, setLoading] = useState(true);
  const [currencyChecked, setCurrencyChecked] = useState(false);
  const [error, setError] = useState(null);
  
  // Use ref to track if we're still mounted
  const isMounted = useRef(true);
  const globalTimeoutRef = useRef(null);
  const startTimeRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    
    // Only check currency once on mount
    if (!currencyChecked) {
      fetchCurrencyPreference();
    }
    
    return () => {
      isMounted.current = false;
      if (globalTimeoutRef.current) {
        clearTimeout(globalTimeoutRef.current);
      }
    };
  }, [currencyChecked]);

  const fetchCurrencyPreference = async (retryCount = 0) => {
    console.log(`💱 CurrencyContext: Fetching currency preference (attempt ${retryCount + 1}/${MAX_RETRIES + 1})...`);
    
    // CRITICAL FIX: Set up GLOBAL timeout that covers ALL retries
    if (retryCount === 0) {
      startTimeRef.current = Date.now();
      
      globalTimeoutRef.current = setTimeout(() => {
        if (isMounted.current && loading && !currencyChecked) {
          console.error('⏱️ Currency fetch GLOBAL TIMEOUT - forcing fallback');
          handleFallback('Currency fetch timed out after all retries');
        }
      }, CURRENCY_TIMEOUT);
    }
    
    // Check if we've exceeded total timeout during retries
    if (startTimeRef.current && Date.now() - startTimeRef.current > CURRENCY_TIMEOUT) {
      console.error('⏱️ Total time exceeded during retry');
      handleFallback('Total timeout exceeded');
      return;
    }

    try {
      console.log('📡 Fetching from:', `${API_BASE_URL}/currency/preference/`);
      
      // Shorter individual request timeout
      const controller = new AbortController();
      const requestTimeout = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch(`${API_BASE_URL}/currency/preference/`, {
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        signal: controller.signal,
      });
      
      clearTimeout(requestTimeout);
      
      if (!isMounted.current) return; // Component unmounted
      
      console.log('📨 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Currency preference data:', data);
        
        const userCurrency = data.currency_code || data.currency?.code || 'NGN';
        console.log('💱 Setting currency to:', userCurrency);
        
        setCurrency(userCurrency);
        setError(null);
        
        // Success - clear timeout and mark as checked
        if (globalTimeoutRef.current) {
          clearTimeout(globalTimeoutRef.current);
        }
        setLoading(false);
        setCurrencyChecked(true);
        console.log('✅ Currency loading complete. Currency is:', userCurrency);
        
      } else if (response.status === 401) {
        // User not logged in - this is normal on landing page
        console.log('ℹ️ User not authenticated - using default NGN');
        
        if (globalTimeoutRef.current) {
          clearTimeout(globalTimeoutRef.current);
        }
        setCurrency('NGN');
        setError(null);
        setLoading(false);
        setCurrencyChecked(true);
        
      } else {
        console.log('⚠️ Response not OK. Status:', response.status);
        
        // Retry on server errors (5xx) only
        if (response.status >= 500 && retryCount < MAX_RETRIES) {
          throw new Error(`Server error: ${response.status}`);
        }
        
        // Client error - don't retry, use fallback
        if (globalTimeoutRef.current) {
          clearTimeout(globalTimeoutRef.current);
        }
        setCurrency('NGN');
        setError(`HTTP ${response.status}`);
        setLoading(false);
        setCurrencyChecked(true);
      }
      
    } catch (error) {
      if (!isMounted.current) return;
      
      console.error('❌ Error fetching currency preference:', error.message);
      
      // Check if we should retry
      const shouldRetry = retryCount < MAX_RETRIES && 
                         (error.name === 'AbortError' || 
                          error.message.includes('Server error') ||
                          error.message.includes('network'));
      
      if (shouldRetry) {
        console.log(`🔄 Retrying in ${RETRY_DELAY}ms... (${retryCount + 1}/${MAX_RETRIES})`);
        setError(`Retry ${retryCount + 1}/${MAX_RETRIES}`);
        
        // Schedule retry
        setTimeout(() => {
          if (isMounted.current) {
            fetchCurrencyPreference(retryCount + 1);
          }
        }, RETRY_DELAY);
      } else {
        // Max retries reached or non-retryable error
        console.log('⚠️ No more retries - using fallback');
        handleFallback(error.message);
      }
    }
  };

  const handleFallback = (errorMessage) => {
    if (!isMounted.current) return;
    
    console.log('⚠️ Using fallback currency: NGN');
    
    if (globalTimeoutRef.current) {
      clearTimeout(globalTimeoutRef.current);
    }
    
    setCurrency('NGN');
    setError(errorMessage);
    setLoading(false);
    setCurrencyChecked(true);
  };

  const toggleCurrency = async () => {
    const newCurrency = currency === 'NGN' ? 'USD' : 'NGN';
    console.log(`🔄 Toggling currency to: ${newCurrency}`);
    
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      
      const response = await fetch(`${API_BASE_URL}/currency/toggle/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ currency: newCurrency }),
        signal: controller.signal,
      });
      
      clearTimeout(timeout);
      
      if (response.ok) {
        setCurrency(newCurrency);
        setError(null);
        
        // Dispatch custom event
        window.dispatchEvent(new CustomEvent('currencyChange', {
          detail: { currency: newCurrency }
        }));
        
        console.log('✅ Currency toggled to:', newCurrency);
        
        // Reload page after short delay
        setTimeout(() => {
          window.location.reload();
        }, 100);
      } else if (response.status === 401) {
        console.log('🔒 401 Unauthorized - Session expired');
        window.location.href = '/';
      } else {
        console.error('❌ Failed to update currency preference');
        setError('Failed to toggle currency');
      }
    } catch (error) {
      console.error('❌ Error toggling currency:', error.message);
      setError(error.message);
      
      // Fallback: Still toggle locally even if backend fails
      console.log('⚠️ Toggling locally as fallback');
      setCurrency(newCurrency);
    }
  };

  const formatCurrency = (amount) => {
    const numAmount = parseFloat(amount) || 0;
    
    try {
      if (currency === 'NGN') {
        return new Intl.NumberFormat('en-NG', {
          style: 'currency',
          currency: 'NGN',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numAmount);
      } else {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(numAmount);
      }
    } catch (error) {
      console.error('❌ Error formatting currency:', error);
      // Fallback formatting
      const symbol = currency === 'NGN' ? '₦' : '$';
      return `${symbol}${numAmount.toFixed(2)}`;
    }
  };

  const getCurrencySymbol = () => {
    return currency === 'NGN' ? '₦' : '$';
  };

  const value = {
    currency,
    loading,
    error,
    toggleCurrency,
    formatCurrency,
    getCurrencySymbol,
    setCurrency: async (newCurrency) => {
      if (['NGN', 'USD'].includes(newCurrency) && newCurrency !== currency) {
        await toggleCurrency();
      }
    }
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};